import { Link } from "react-router-dom";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Platform", to: "/platform" },
  { label: "Pricing", to: "/pricing" },
  { label: "Company", to: "/company" },
  { label: "Contact", to: "/contact" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms of Service", to: "/terms" },
];

const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://linkedin.com/company/vawena",
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    label: "Twitter / X",
    href: "https://twitter.com/vawena_ai",
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    label: "Instagram",
    href: "https://instagram.com/vawena_ai",
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
];

export const Footer = () => (
  <footer className="dark:bg-[#020D30] bg-white border-t dark:border-white/[0.06] border-gray-200">
    {/* Main footer body — outer padding matches platform sections + header rhythm */}
    <div className="px-4 sm:px-8 lg:px-16">
      <div className="max-w-[1200px] mx-auto py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">

        {/* Brand column */}
        <div className="md:col-span-2">
          <Link to="/" className="inline-block mb-4">
            <img src="/assets/logo/vawena-logo.png" alt="VaWeNa" width={120} height={32} className="h-8 w-auto hidden dark:block" />
            <img src="/assets/logo/vawena-logo-light.png" alt="VaWeNa" width={120} height={32} className="h-8 w-auto block dark:hidden"
              onError={(e) => { (e.target as HTMLImageElement).src = '/assets/logo/vawena-logo.png'; }} />
          </Link>
          <p className="text-sm dark:text-slate-400 text-gray-500 max-w-[280px] leading-relaxed mb-6">
            AI-powered financial intelligence for real estate investors. Track net worth, analyze portfolio performance, and generate lender-ready reports.
          </p>
          {/* Social icons */}
          <div className="flex gap-3">
            {SOCIAL_LINKS.map(s => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                aria-label={s.label}
                className="w-8 h-8 flex items-center justify-center rounded-lg dark:bg-white/[0.06] bg-gray-100 dark:text-slate-400 text-gray-500 dark:hover:bg-white/[0.12] hover:bg-gray-200 dark:hover:text-white hover:text-gray-800 transition-all duration-200">
                {s.icon}
              </a>
            ))}
          </div>
        </div>

        {/* Platform links */}
        <div>
          <h4 className="text-xs font-bold tracking-widest uppercase dark:text-slate-500 text-gray-400 mb-4">Platform</h4>
          <ul className="space-y-3">
            {NAV_LINKS.map(({ label, to }) => (
              <li key={to}>
                <Link to={to}
                  className="text-sm dark:text-slate-400 text-gray-600 dark:hover:text-white hover:text-gray-900 transition-colors duration-200">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Legal + product */}
        <div>
          <h4 className="text-xs font-bold tracking-widest uppercase dark:text-slate-500 text-gray-400 mb-4">Legal</h4>
          <ul className="space-y-3 mb-8">
            {LEGAL_LINKS.map(({ label, to }) => (
              <li key={to}>
                <Link to={to}
                  className="text-sm dark:text-slate-400 text-gray-600 dark:hover:text-white hover:text-gray-900 transition-colors duration-200">
                  {label}
                </Link>
              </li>
            ))}
          </ul>

          <h4 className="text-xs font-bold tracking-widest uppercase dark:text-slate-500 text-gray-400 mb-4">Modules</h4>
          <ul className="space-y-3">
            {[
              { label: "WealthScope", to: "/platform#wealthscope" },
              { label: "PortfolioIQ", to: "/platform#portfolioiq" },
              { label: "Bundle", to: "/pricing" },
            ].map(({ label, to }) => (
              <li key={to}>
                <Link to={to}
                  className="text-sm dark:text-slate-400 text-gray-600 dark:hover:text-white hover:text-gray-900 transition-colors duration-200">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        </div>
      </div>
    </div>

    {/* Bottom bar */}
    <div className="border-t dark:border-white/[0.06] border-gray-200">
      <div className="px-4 sm:px-8 lg:px-16">
        <div className="max-w-[1200px] mx-auto py-5 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-center sm:gap-4 text-center sm:text-left">
          <p className="text-xs dark:text-slate-500 text-gray-400 sm:justify-self-start">
            © 2026 VaWeNa, LLC. All rights reserved.
          </p>
          <div className="flex items-center justify-center gap-2 sm:justify-self-center">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-xs dark:text-slate-500 text-gray-400">All systems operational</span>
          </div>
          <div className="flex gap-5 text-xs dark:text-slate-500 text-gray-400 justify-center sm:justify-self-end">
            <Link to="/privacy-policy" className="dark:hover:text-white hover:text-gray-900 transition-colors">Privacy</Link>
            <Link to="/terms" className="dark:hover:text-white hover:text-gray-900 transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </div>
  </footer>
);
